<script setup lang="ts">
import { ExternalLink, FileText, Plus, Quote } from 'lucide-vue-next';
import { computed } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export interface CslJson {
    type: string;
    title?: string;
    author?: Array<{ family?: string; given?: string; literal?: string }>;
    editor?: Array<{ family?: string; given?: string }>;
    issued?: { 'date-parts'?: number[][] };
    DOI?: string;
    ISBN?: string;
    ISSN?: string;
    URL?: string;
    'container-title'?: string; // journal / book title for chapters
    'collection-title'?: string; // series
    volume?: string;
    issue?: string;
    page?: string;
    publisher?: string;
    'publisher-place'?: string;
    edition?: string;
    genre?: string; // thesis type
    source?: string;
    [key: string]: unknown;
}

export interface ReferenceItem {
    id: string;
    title: string;
    authors: string[] | string;
    abstract: string | null;
    year: number | null;
    doi: string | null;
    source: string | null;
    type: string;
    csl: CslJson | null;
    has_pdf: boolean;
    key: string;
    collections: string[];
}

const props = defineProps<{
    reference: ReferenceItem;
    density?: 'compact' | 'comfortable';
    citedCount?: number;
    articleUrl?: string;
}>();

const emit = defineEmits<{
    cite: [ref: ReferenceItem];
}>();

const authorsDisplay = computed(() => {
    const authors = Array.isArray(props.reference.authors)
        ? props.reference.authors
        : [props.reference.authors];
    const first3 = authors.slice(0, 3).join(', ');

    return authors.length > 3 ? first3 + ' et al.' : first3;
});

const isCompact = computed(() => props.density === 'compact');
</script>

<template>
    <li
        class="group relative px-3 transition hover:bg-muted/50"
        :class="[
            isCompact ? 'py-2.5' : 'py-3',
            (citedCount ?? 0) > 0
                ? 'border-l-2 border-l-violet-500'
                : 'border-l-2 border-l-transparent',
        ]"
    >
        <!-- Title -->
        <p
            class="leading-snug font-medium text-foreground"
            :class="isCompact ? 'line-clamp-2 text-xs' : 'line-clamp-3 text-sm'"
        >
            {{ reference.title }}
        </p>

        <!-- Authors + year -->
        <p
            class="mt-0.5 text-muted-foreground"
            :class="isCompact ? 'text-[11px]' : 'text-xs'"
        >
            {{ authorsDisplay
            }}<span v-if="reference.year"> · {{ reference.year }}</span>
        </p>

        <!-- Badges + cite row -->
        <div class="mt-1.5 flex items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-1">
                <Badge
                    v-if="reference.source === 'zotero'"
                    variant="secondary"
                    :class="isCompact ? 'px-1.5 py-0 text-[10px]' : 'text-xs'"
                >
                    Zotero
                </Badge>
                <Badge
                    v-if="reference.source === 'manual'"
                    variant="outline"
                    :class="isCompact ? 'px-1.5 py-0 text-[10px]' : 'text-xs'"
                >
                    Manuel
                </Badge>
                <Badge
                    v-if="reference.has_pdf"
                    variant="secondary"
                    :class="isCompact ? 'px-1.5 py-0 text-[10px]' : 'text-xs'"
                >
                    <FileText class="mr-0.5 h-2.5 w-2.5" />
                    PDF
                </Badge>
                <Badge
                    v-if="(citedCount ?? 0) > 0"
                    variant="default"
                    :class="isCompact ? 'px-1.5 py-0 text-[10px]' : 'text-xs'"
                >
                    <Quote class="mr-0.5 h-2.5 w-2.5" />
                    Cité ×{{ citedCount }}
                </Badge>
            </div>

            <!-- Cite button — inline, right-aligned, hover only -->
            <Button
                data-tour="reference-cite-btn"
                size="sm"
                variant="outline"
                :class="[
                    'shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100',
                    isCompact ? 'h-5 px-1.5 text-[10px]' : 'h-6 px-2 text-xs',
                ]"
                @click.stop="emit('cite', reference)"
            >
                <Plus :class="isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3'" />
                Citer
            </Button>
        </div>

        <!-- DOI link (comfortable only) -->
        <a
            v-if="!isCompact && reference.doi"
            :href="`https://doi.org/${reference.doi}`"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
            @click.stop
        >
            <ExternalLink class="h-3 w-3" />
            DOI
        </a>
    </li>
</template>
